import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import type { ProviderSummary } from '../../types/billing';

interface Props {
  providers: ProviderSummary[];
  onRowClick: (provider: ProviderSummary) => void;
}

const Toolbar: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
    <GridToolbarQuickFilter placeholder="Search providers…" debounceMs={200} />
  </Box>
);

const ProvidersTable: React.FC<Props> = ({ providers, onRowClick }) => {
  const columns: GridColDef<ProviderSummary>[] = [
    {
      field: 'name',
      headerName: 'Provider',
      flex: 1.2,
      minWidth: 200,
      valueGetter: (_v, row) => `${row.firstName} ${row.lastName}`,
    },
    {
      field: 'npi',
      headerName: 'NPI',
      width: 130,
      valueGetter: (_v, row) => row.npi || '-',
    },
    {
      field: 'specialty',
      headerName: 'Specialty',
      width: 160,
      valueGetter: (_v, row) => row.specialty || '-',
    },
    {
      field: 'practiceName',
      headerName: 'Practice',
      flex: 1,
      minWidth: 180,
      valueGetter: (_v, row) => row.practiceName,
    },
    {
      field: 'billerNames',
      headerName: 'Billers',
      flex: 1,
      minWidth: 160,
      valueGetter: (_v, row) => row.billerNames.join(', ') || '-',
    },
    {
      field: 'credentialingStatus',
      headerName: 'Status',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<ProviderSummary>) => {
        const status = params.row.credentialingStatus;
        if (status === 'ACTIVE') {
          return <Chip label="Active" size="small" color="success" sx={{ fontWeight: 600 }} />;
        }
        if (status === 'PENDING') {
          return (
            <Chip
              label="Pending"
              size="small"
              sx={{ backgroundColor: '#fff3e0', color: '#bf5b00', fontWeight: 700 }}
            />
          );
        }
        return <Typography variant="caption" color="text.secondary">-</Typography>;
      },
    },
  ];

  return (
    <DataGrid<ProviderSummary>
      rows={providers}
      columns={columns}
      getRowId={(row) => row.id}
      disableRowSelectionOnClick
      onRowClick={(params) => onRowClick(params.row)}
      initialState={{
        pagination: { paginationModel: { pageSize: 25, page: 0 } },
        sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
      }}
      pageSizeOptions={[10, 25, 50, 100]}
      slots={{ toolbar: Toolbar }}
      sx={{
        border: 0,
        '& .MuiDataGrid-row': { cursor: 'pointer' },
        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5', fontWeight: 600 },
      }}
    />
  );
};

export default ProvidersTable;
